import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as S from './AdvancesStyles';

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const fmtDate = d => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};
const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);

// "Nice" round ceiling for an axis max: e.g. 8,700 -> 10,000, 23,000 -> 25,000.
// Keeps gridline labels the kind of round numbers a person expects on an axis.
const niceCeil = x => {
  if (x <= 0) return 1000;
  const exp = Math.floor(Math.log10(x));
  const base = Math.pow(10, exp);
  const frac = x / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
};

/* --- Interactive SVG Vertical Bar Chart for Apiaries with Tooltip --- */
const ApiaryBarChartSVG = ({ data }) => {
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '30px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
        No apiaries linked to this project.
      </div>
    );
  }

  // Plain linear scale — the axis gridlines and the bar heights use the exact same mapping,
  // so a gridline value always lines up with the bar that reaches it. A nonzero amount still
  // gets a small minimum-height floor so it stays visible/hoverable even when it's tiny next
  // to a much larger apiary on the same chart, without distorting the axis itself.
  const realMax = Math.max(...data.map(x => x.total), 0);
  const tickMax = niceCeil(realMax * 1.08 || 1000);
  const tickStep = tickMax / 5;
  const ticks = [0, 1, 2, 3, 4, 5].map(i => Math.round(i * tickStep));

  const leftAxisW = 84;
  const topPad = 14;
  const plotH = 200;
  const xAxisH = 74;
  const height = topPad + plotH + xAxisH;
  const colW = 70;
  const plotW = Math.max(data.length * colW, 300);
  const width = leftAxisW + plotW;
  const baselineY = topPad + plotH;
  const MIN_BAR_PX = 4;

  const yFor = v => baselineY - Math.min(v, tickMax) / tickMax * plotH;

  const handleMouseMove = (e, item) => {
    const rect = e.currentTarget.closest('.apiary-bar-container').getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setHoveredBar(item);
  };

  return (
    <div className="apiary-bar-container" style={{ position: 'relative', width: '100%', overflowX: 'auto', padding: '10px 0' }}>
      {/* Floating Tooltip */}
      {hoveredBar && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: 'translate(-50%, -120%)',
            background: 'rgba(24, 26, 32, 0.96)',
            color: '#FFFFFF',
            padding: '9px 13px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            pointerEvents: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            zIndex: 100,
            whiteSpace: 'nowrap'
          }}
        >
          <div style={{ fontSize: '12px', color: '#FFFFFF', fontWeight: 700, marginBottom: '3px' }}>{hoveredBar.a.name}</div>
          <div style={{ fontSize: '14px', fontWeight: 800, color: '#60A5FA' }}>{fmt(hoveredBar.total)}</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
            {hoveredBar.count} expense{hoveredBar.count === 1 ? '' : 's'} logged
          </div>
          {(hoveredBar.a.district || hoveredBar.a.state) && (
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
              {[hoveredBar.a.district, hoveredBar.a.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )}

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
        {/* Gridlines + axis labels */}
        {ticks.map((t, i) => {
          const ty = yFor(t);
          return (
            <g key={i}>
              <line x1={leftAxisW} y1={ty} x2={width} y2={ty} stroke="#E7E8EC" strokeWidth="1" />
              <text x={leftAxisW - 10} y={ty + 4} textAnchor="end" fontSize="11" fontWeight="600" fill="#94A3B8">
                {t.toLocaleString('en-IN')}
              </text>
            </g>
          );
        })}
        {/* Y-axis line */}
        <line x1={leftAxisW} y1={topPad} x2={leftAxisW} y2={baselineY} stroke="#D1D5DB" strokeWidth="1.5" />
        {/* X-axis (baseline) */}
        <line x1={leftAxisW} y1={baselineY} x2={width} y2={baselineY} stroke="#D1D5DB" strokeWidth="1.5" />

        {data.map((item, idx) => {
          const colWidth = colW;
          const barWidth = Math.min(colWidth * 0.55, 40);
          const x = leftAxisW + idx * colWidth + (colWidth - barWidth) / 2;
          const rawHeight = (item.total / tickMax) * plotH;
          const barHeight = item.total > 0 ? Math.max(rawHeight, MIN_BAR_PX) : 0;
          const y = baselineY - barHeight;
          const isHovered = hoveredBar?.a.id === item.a.id;

          return (
            <g
              key={idx}
              style={{ cursor: 'pointer' }}
              onMouseMove={e => handleMouseMove(e, item)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              <rect
                x={leftAxisW + idx * colWidth}
                y={topPad}
                width={colWidth}
                height={plotH + xAxisH}
                fill="transparent"
              />
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={isHovered ? '#1D4ED8' : '#2F6FED'}
                rx="4"
                style={{
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: isHovered ? 'drop-shadow(0 4px 10px rgba(47, 111, 237, 0.4))' : 'none'
                }}
              />
              <text
                x={x + barWidth / 2}
                y={baselineY + 14}
                textAnchor="end"
                transform={`rotate(-40 ${x + barWidth / 2} ${baselineY + 14})`}
                fontSize="11"
                fontWeight="600"
                fill={isHovered ? '#181A20' : '#6B7280'}
              >
                {(() => {
                  // Labels are drawn on a diagonal (not horizontal) specifically because a
                  // horizontal label can only use its own ~70px column before it runs into
                  // the neighbor — with 20+ apiaries that always overlaps. Rotated, a label
                  // has the whole diagonal run to itself, so it needs far less truncation.
                  // Full name is still always available via hover (see tooltip above).
                  const maxChars = 16;
                  return item.a.name.length > maxChars
                    ? item.a.name.slice(0, maxChars - 1) + '…'
                    : item.a.name;
                })()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const ProjectPnLTab = ({
  txns,
  projects,
  apiaries,
  member,
  onOpenPassbook
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    const q = searchQuery.toLowerCase().trim();
    return projects.filter(p => p.name.toLowerCase().includes(q) || String(p.id).toLowerCase().includes(q));
  }, [projects, searchQuery]);

  const isAll = selectedProjectId === 'ALL' || !selectedProjectId;
  const activeProjectObj = projects.find(
    p => String(p.id).toLowerCase() === String(selectedProjectId).toLowerCase()
  );

  // Filter apiaries for the selected project
  const projApiaries = useMemo(() => {
    if (isAll) return apiaries;
    const targetProjId = String(selectedProjectId).toLowerCase();
    const projObj = projects.find(p => String(p.id).toLowerCase() === targetProjId);
    const pName = (projObj?.name || '').toLowerCase();

    return apiaries.filter(a => {
      if (String(a.projectId || '').toLowerCase() === targetProjId) return true;
      if (String(a.id || '').toLowerCase() === targetProjId) return true;
      
      const aState = String(a.state || '').toLowerCase();
      const aDistrict = String(a.district || '').toLowerCase();
      const aName = String(a.name || '').toLowerCase();

      // Check if project states / districts match apiary
      if (projObj?.states && projObj.states.some(s => s && (aState.includes(s) || s.includes(aState) || aName.includes(s)))) {
        return true;
      }
      if (projObj?.districts && projObj.districts.some(d => d && (aDistrict.includes(d) || d.includes(aDistrict) || aName.includes(d)))) {
        return true;
      }

      if ((pName.includes('tgserp') || pName.includes('telangana')) && 
          (aState.includes('telangana') || aDistrict.includes('telangana') || aName.includes('tgserp') || aName.includes('telangana') || aName.includes('chinturu') || aName.includes('adilaraghat'))) {
        return true;
      }
      if ((pName.includes('muzaffarpur') || pName.includes('bihar')) && 
          (aDistrict.includes('muzaffarpur') || aState.includes('bihar') || aName.includes('muzaffarpur'))) {
        return true;
      }
      if (pName.includes('betul') && 
          (aDistrict.includes('betul') || aState.includes('madhya') || aName.includes('betul'))) {
        return true;
      }
      if (pName.includes('varanasi') && 
          (aDistrict.includes('varanasi') || aState.includes('uttar') || aName.includes('varanasi'))) {
        return true;
      }
      return false;
    });
  }, [apiaries, selectedProjectId, isAll, projects]);

  const apiaryCosts = useMemo(() => {
    return projApiaries.map(a => {
      const aId = String(a.id || '').toLowerCase();
      const aApiaryId = String(a.apiary_id || '').toLowerCase();
      const aMongoId = String(a.mongoId || '').toLowerCase();

      const matchedExpenses = txns.filter(t => {
        if (t.type !== 'expense' || t.tagType !== 'apiary' || t.approvalStatus === 'rejected') {
          return false;
        }
        const tLoc = String(t.locId || t.apiaryId || '').toLowerCase();
        return tLoc === aId || (aApiaryId && tLoc === aApiaryId) || (aMongoId && tLoc === aMongoId);
      });

      return {
        a,
        total: sum(matchedExpenses, t => t.amount),
        count: matchedExpenses.length
      };
    });
  }, [projApiaries, txns]);

  const apiaryTotal = sum(apiaryCosts, x => x.total);

  // Filter non-apiary overhead expenses for the selected project
  const overheadRows = useMemo(() => {
    if (isAll) {
      return txns.filter(
        t =>
          t.type === 'expense' &&
          (t.tagType === 'project_overhead' || t.tagType === 'non_apiary') &&
          t.approvalStatus !== 'rejected'
      );
    }
    const targetProjId = String(selectedProjectId).toLowerCase();
    const projObj = projects.find(p => String(p.id).toLowerCase() === targetProjId);
    const pName = (projObj?.name || '').toLowerCase();

    return txns.filter(t => {
      if (t.type !== 'expense') return false;
      if (t.tagType !== 'project_overhead' && t.tagType !== 'non_apiary') return false;
      if (t.approvalStatus === 'rejected') return false;

      if (String(t.projectId || '').toLowerCase() === targetProjId) return true;
      if (String(t.locId || '').toLowerCase() === targetProjId) return true;

      // Geographical / Name fallback matching
      const tDetails = `${t.comment || ''} ${t.notes || ''} ${t.memberName || ''} ${t.rawExpense?.state || ''} ${t.rawExpense?.district || ''}`.toLowerCase();
      
      if (projObj?.states && projObj.states.some(s => s && tDetails.includes(s))) {
        return true;
      }
      if (projObj?.districts && projObj.districts.some(d => d && tDetails.includes(d))) {
        return true;
      }

      if ((pName.includes('tgserp') || pName.includes('telangana')) && (tDetails.includes('telangana') || tDetails.includes('tgserp'))) {
        return true;
      }
      if ((pName.includes('muzaffarpur') || pName.includes('bihar')) && (tDetails.includes('muzaffarpur') || tDetails.includes('bihar'))) {
        return true;
      }
      if (pName.includes('betul') && (tDetails.includes('betul') || tDetails.includes('madhya'))) {
        return true;
      }
      if (pName.includes('varanasi') && (tDetails.includes('varanasi') || tDetails.includes('uttar'))) {
        return true;
      }
      return false;
    });
  }, [txns, selectedProjectId, isAll, projects]);

  const overheadTotal = sum(overheadRows, t => t.amount);

  return (
    <div>
      <S.FiltersBar style={{ display: 'flex', gap: '14px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {/* Single Unified Searchable Project Selector */}
        <div ref={dropdownRef} style={{ position: 'relative', width: '380px', maxWidth: '100%' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: '6px' }}>
            Choose Project
          </label>
          <div
            onClick={() => setIsOpen(!isOpen)}
            style={{
              padding: '9px 12px',
              border: '1px solid #E7E8EC',
              borderRadius: '8px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 600,
              fontSize: '13px',
              color: '#1E2130',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span>{isAll ? '🌟' : '📁'}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isAll ? 'All Projects (Overall Rollup)' : activeProjectObj?.name || selectedProjectId}
              </span>
            </div>
            <span style={{ fontSize: '10px', color: '#94A3B8', marginLeft: '6px' }}>▼</span>
          </div>

          {/* Inline Searchable Dropdown Popup */}
          {isOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                background: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                zIndex: 1000,
                overflow: 'hidden'
              }}
            >
              {/* Search Input */}
              <div style={{ padding: '8px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: '6px', background: '#FAFAFA' }}>
                <span style={{ fontSize: '12px', color: '#94A3B8' }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search project name..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '12.5px',
                    outline: 'none',
                    padding: '3px 0'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={e => { e.stopPropagation(); setSearchQuery(''); }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', color: '#94A3B8' }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Options List */}
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                <div
                  onClick={() => { setSelectedProjectId('ALL'); setIsOpen(false); }}
                  style={{
                    padding: '8px 12px',
                    fontSize: '12.5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: isAll ? '#F0FDF4' : 'transparent',
                    color: isAll ? '#16A34A' : '#1E293B',
                    fontWeight: isAll ? 700 : 500,
                    borderBottom: '1px solid #F8FAFC'
                  }}
                  onMouseEnter={e => !isAll && (e.currentTarget.style.background = '#F8FAFC')}
                  onMouseLeave={e => !isAll && (e.currentTarget.style.background = 'transparent')}
                >
                  <span>🌟 All Projects (Overall Rollup)</span>
                  {isAll && <span>✓</span>}
                </div>

                {filteredProjects.map(p => {
                  const isSelected = String(p.id) === String(selectedProjectId);
                  return (
                    <div
                      key={p.id}
                      onClick={() => { setSelectedProjectId(p.id); setIsOpen(false); }}
                      style={{
                        padding: '8px 12px',
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: isSelected ? '#EFF6FF' : 'transparent',
                        color: isSelected ? '#2563EB' : '#1E293B',
                        fontWeight: isSelected ? 700 : 500
                      }}
                      onMouseEnter={e => !isSelected && (e.currentTarget.style.background = '#F8FAFC')}
                      onMouseLeave={e => !isSelected && (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span>📁</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                      </div>
                      {isSelected && <span>✓</span>}
                    </div>
                  );
                })}

                {filteredProjects.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#94A3B8' }}>
                    No projects found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {selectedProjectId !== 'ALL' && (
          <S.Btn ghost sm onClick={() => { setSelectedProjectId('ALL'); setSearchQuery(''); }}>
            Reset to All Projects
          </S.Btn>
        )}
      </S.FiltersBar>

      <div style={{ marginBottom: '14px', fontSize: '13px', color: '#64748b' }}>
        Showing financial breakdown for:{' '}
        <strong style={{ color: '#1e293b' }}>
          {isAll ? '🌟 All Projects Rollup' : `📁 ${activeProjectObj?.name || selectedProjectId}`}
        </strong>
      </div>

      <S.KpiRow cols={3}>
        <S.KpiCard accent>
          <div className="lbl">Total Project Cost</div>
          <div className="val">{fmt(apiaryTotal + overheadTotal)}</div>
          <div className="sub">apiaries + overhead</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Apiary Costs</div>
          <div className="val">{fmt(apiaryTotal)}</div>
          <div className="sub">across {projApiaries.length} apiaries</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Non-Apiary Overhead</div>
          <div className="val">{fmt(overheadTotal)}</div>
          <div className="sub">not tied to one apiary</div>
        </S.KpiCard>
      </S.KpiRow>

      <S.CardBox style={{ marginBottom: '16px' }}>
        <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Cost by apiary within project</span>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
            {projApiaries.length} Apiaries
          </span>
        </h3>
        <div className="hint">Bars are drawn to scale against the axis on the left — hover a bar for the exact amount, expense count and location. Scroll right if there are more apiaries than fit on screen.</div>
        <ApiaryBarChartSVG data={apiaryCosts} />
      </S.CardBox>

      <div style={{ fontSize: '13px', fontWeight: 800, margin: '22px 0 10px', color: '#4B4F5C', textTransform: 'uppercase' }}>
        Non-apiary / overhead costs for this project ({overheadRows.length})
      </div>

      <S.TableWrap>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Member</th>
              <th>Amount</th>
              <th>Category</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {overheadRows.length ? (
              overheadRows.map(t => {
                const m = member(t.memberId);
                return (
                  <tr key={t.id}>
                    <td>{fmtDate(t.date)}</td>
                    <td>
                      <span
                        className="member-link"
                        onClick={() => onOpenPassbook(m?.id || t.memberId)}
                      >
                        {m?.name || t.memberName || 'Staff'}
                      </span>
                    </td>
                    <td>
                      <strong>{fmt(t.amount)}</strong>
                    </td>
                    <td>
                      <S.Chip>{t.category}</S.Chip>
                    </td>
                    <td>
                      <S.Pill variant={t.approvalStatus}>
                        {t.approvalStatus.charAt(0).toUpperCase() + t.approvalStatus.slice(1)}
                      </S.Pill>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  No project-level overhead logged for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </S.TableWrap>
    </div>
  );
};

export default ProjectPnLTab;
